import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import "./Modal.css";

interface Props {
  open: boolean;
  label: string;
  onClose: () => void;
  children: ReactNode;
  /** Focused when the dialog opens. */
  initialFocus?: RefObject<HTMLElement | null>;
}

/** Backdrop + dialog. Escape and a backdrop tap close it; focus returns to where it was. */
export function Modal({ open, label, onClose, children, initialFocus }: Props) {
  const previous = useRef<HTMLElement | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    previous.current = document.activeElement as HTMLElement | null;
    initialFocus?.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Only the topmost dialog closes (a picker over a card closes alone).
      const all = document.querySelectorAll(".modal");
      if (all[all.length - 1] === box.current?.parentElement) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous.current?.focus?.();
    };
  }, [open, onClose, initialFocus]);

  if (!open) return null;
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className="modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={box} role="dialog" aria-modal="true" aria-label={label} className="modal-box">
        {children}
      </div>
    </div>
  );
}
